import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, LogOut, Search, Trash2, RefreshCw, Download, Send } from "lucide-react";
import { fetchWaitlist, deleteWaitlist, resendWelcome, errorMessage } from "../lib/api";

const KEY = "hc_admin_key";

const cell = (v) => {
  const s = String(v ?? "");
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return `"${safe.replace(/"/g, '""')}"`;
};

const exportCsv = (rows) => {
  const head = ["Name", "Email", "Household size", "Interests", "Note", "Welcome email", "Joined (UTC)"];
  const lines = rows.map((r) => [r.name, r.email, r.household_size, (r.interests ?? []).join("; "), r.note, r.email_status, r.created_at].map(cell).join(","));
  const blob = new Blob(["\ufeff" + [head.map(cell).join(","), ...lines].join("\r\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `honestchoice-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const STATUS = { sent: "text-emerald-300", sending: "text-slate-300", failed: "text-rose-300", paused: "text-amber-300" };

export default function AdminWaitlist() {
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY) ?? "");
  const [draft, setDraft] = useState("");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (k = key) => {
    setLoading(true);
    try {
      setRows(await fetchWaitlist(k));
      sessionStorage.setItem(KEY, k);
      setKey(k);
    } catch (e) {
      toast.error(errorMessage(e));
      sessionStorage.removeItem(KEY);
      setKey("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (key) load(key); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resend = async (id) => {
    try {
      const { email_status } = await resendWelcome(key, id);
      setRows((r) => r.map((x) => (x.id === id ? { ...x, email_status } : x)));
      if (email_status === "sent") toast.success("Welcome email sent");
      else toast.error(email_status === "paused" ? "Email paused: top up your Universal Key balance" : "Email failed to send");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const remove = async (id) => {
    try { await deleteWaitlist(key, id); setRows((r) => r.filter((x) => x.id !== id)); toast.success("Entry removed"); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  if (!key) {
    return (
      <section className="mx-auto grid min-h-[80vh] max-w-md place-items-center px-5 pt-28">
        <form onSubmit={(e) => { e.preventDefault(); load(draft); }} data-testid="admin-login-form" className="glass w-full rounded-3xl p-8">
          <Lock className="h-8 w-8 text-cyan-300" />
          <h1 className="mt-4 font-display text-3xl font-bold text-white">Waitlist admin</h1>
          <p className="mt-2 text-sm text-slate-300">Enter the admin passcode to view sign-ups.</p>
          <input type="password" value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="admin-passcode-input" placeholder="Passcode" className="mt-6 w-full rounded-xl border border-cyan-400/25 bg-deep/70 px-4 py-3 text-white focus:border-cyan-300 focus:outline-none" />
          <button disabled={loading || !draft} data-testid="admin-login-btn" className="btn-primary mt-4 w-full disabled:opacity-50">View waitlist</button>
        </form>
      </section>
    );
  }

  const shown = rows.filter((r) => `${r.name} ${r.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <section data-testid="admin-waitlist-page" className="mx-auto max-w-[1440px] px-5 pb-24 pt-28 lg:px-12 lg:pt-36">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div><p className="eyebrow">Admin</p><h1 className="mt-2 font-display text-4xl font-bold text-white">Waitlist <span className="text-grad" data-testid="admin-count">{rows.length}</span></h1></div>
        <div className="flex gap-2">
          <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search-input" placeholder="Search" className="rounded-full border border-cyan-400/25 bg-deep/70 py-2.5 pl-9 pr-4 text-sm text-white focus:border-cyan-300 focus:outline-none" /></div>
          <button onClick={() => exportCsv(rows)} disabled={!rows.length} data-testid="admin-export-btn" className="btn-primary !px-5 !py-2.5 text-sm disabled:opacity-50"><Download className="h-4 w-4" /> Export CSV</button>
          <button onClick={() => load()} data-testid="admin-refresh-btn" aria-label="Refresh" className="btn-ghost !px-3 !py-2.5"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
          <button onClick={() => { sessionStorage.removeItem(KEY); setKey(""); setRows([]); }} data-testid="admin-logout-btn" aria-label="Sign out" className="btn-ghost !px-3 !py-2.5"><LogOut className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="glass mt-8 overflow-x-auto rounded-2xl">
        <table className="w-full min-w-[760px] text-left text-sm" data-testid="admin-waitlist-table">
          <thead className="border-b border-cyan-400/20 font-mono text-xs uppercase tracking-[0.15em] text-cyan-200">
            <tr><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Household</th><th className="p-4">Interests</th><th className="p-4">Welcome email</th><th className="p-4">Joined</th><th className="p-4" /></tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className="border-b border-white/5 align-top text-slate-200">
                <td className="p-4 text-white">{r.name}{r.note && <p className="mt-1 max-w-xs text-xs text-slate-400">“{r.note}”</p>}</td>
                <td className="p-4">{r.email}</td>
                <td className="p-4">{r.household_size ?? "—"}</td>
                <td className="p-4">{(r.interests ?? []).join(", ") || "—"}</td>
                <td className={`p-4 capitalize ${STATUS[r.email_status] ?? "text-slate-400"}`} data-testid={`admin-email-status-${r.id}`}>
                  {r.email_status ?? "—"}
                  {r.email_status && r.email_status !== "sent" && r.email_status !== "sending" && (
                    <button onClick={() => resend(r.id)} data-testid={`admin-resend-${r.id}`} className="ml-2 inline-flex items-center gap-1 rounded-full border border-cyan-400/40 px-2 py-0.5 text-xs normal-case text-cyan-200 hover:bg-cyan-400/10"><Send className="h-3 w-3" />Resend</button>
                  )}
                </td>
                <td className="p-4 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="p-4"><button onClick={() => remove(r.id)} data-testid={`admin-delete-${r.id}`} aria-label="Delete" className="rounded-full p-2 text-rose-300 hover:bg-rose-400/10"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={7} className="p-10 text-center text-slate-300">No sign-ups yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
